import React, {useCallback} from 'react';
import {boardSlice} from '@reducers';
import {useDispatch, useSelector} from '@utils';

export const HighlightText = ({
    text,
    onHover,
    highlightRegex = /([A-HJ-T](?:1[0-9]|[1-9]))/g,
    highlightColor = 'rgba(255, 235, 100, 0.5)',
    textDecorationColor = 'rgba(197, 163, 95, 1)',
}: {
    text: string;
    onHover?: (coord: string | null) => void;
    highlightRegex?: RegExp;
    highlightColor?: string;
    textDecorationColor?: string;
}) => {
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    const regex = new RegExp(highlightRegex.source, highlightRegex.flags);
    const dispatch = useDispatch();
    const {highlights} = useSelector(state => state.board);

    const handleHover = useCallback(
        (coord: string | null) => {
            if (coord) {
                if (!highlights.includes(coord)) {
                    dispatch(boardSlice.actions.setHighlights([coord]));
                }
            } else {
                dispatch(boardSlice.actions.resetHightlight());
            }
        },
        [dispatch, highlights]
    );

    while ((match = regex.exec(text)) !== null) {
        const [coord] = match;
        const index = match.index;

        if (index > lastIndex) {
            parts.push(text.slice(lastIndex, index));
        }

        parts.push(
            <span
                key={index}
                style={{
                    backgroundColor: highlightColor,
                    textDecoration: 'underline',
                    textDecorationStyle: 'dotted',
                    textDecorationColor: textDecorationColor,
                    cursor: 'pointer',
                }}
                onMouseEnter={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleHover(coord);
                    onHover?.(coord);
                }}
                onMouseLeave={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleHover(null);
                    onHover?.(null);
                }}>
                {coord}
            </span>
        );

        lastIndex = index + coord.length;
    }

    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }

    return <span>{parts}</span>;
};
